import { Component } from 'preact'
import { Group, GroupItem, membershipTypes, ORIGIN_GROUP_WIDGET } from '../groups-destination/GroupOverview.sync'
import Button from '../../control-components/ui-components/Button'
import Axios from 'axios'
import { isExternalLink } from '../../utils/url'
import Message from '../design-system-components/message'
import Phrase from '../../libs/translationsUtils/phrase'
import { z } from 'zod'
import WidgetContainer, { widgetContainerConfig, WidgetContainerConfig } from '../widget-container'

interface GroupsWidgetProps extends WidgetContainerConfig {
  groups: Array<{id: number}>
  buttonLabel?: string
  buttonURL?: string
}

interface State {
  groups: Group[]
  successMessageFor: null | membershipTypes
  joinedGroupTitle: string
}

export const widgetName = 'groups'

export const config = z.object({
  groups: z.array(z.object({
    id: z.number()
  })),
  buttonLabel: z.string(),
  buttonURL: z.string()
}).merge(widgetContainerConfig)

export type GroupsWidgetConfig = z.infer<typeof config>

export default class GroupsWidget extends Component<GroupsWidgetProps, State> {
  constructor (props: GroupsWidgetProps) {
    super(props)

    this.state = {
      groups: [],
      successMessageFor: null,
      joinedGroupTitle: ''
    }
  }

  async componentDidMount () {
    const { groups } = this.props
    const res = await Axios.get('/getGroups', {
      params: {
        ids: groups.map(group => group.id)
      }
    })
    if (res.data && (res.data.myGroups.length || res.data.otherGroups.length)) {
      const newGroupList = groups.map((group) => {
        const indexOfMyList = res.data.myGroups.findIndex((myGroup: Group) => myGroup.id === group.id)
        if (indexOfMyList !== -1) {
          return { ...res.data.myGroups[indexOfMyList], ...{ isUserMember: true } }
        }

        const indexOfOtherList = res.data.otherGroups.findIndex((myGroup: Group) => myGroup.id === group.id)
        if (indexOfOtherList === -1) return null

        return { ...res.data.otherGroups[indexOfOtherList], ...{ isUserMember: false } }
      })
      this.setState({ groups: newGroupList.filter(g => g) })
    }
  }

  render () {
    const { title, subtitle, backgroundColor, backgroundColorDisplay, buttonLabel, buttonURL } = this.props
    const { groups, successMessageFor, joinedGroupTitle } = this.state
    const externalLink = typeof window !== 'undefined' ? isExternalLink(buttonURL || '', window.location) : false

    if (!groups.length) {
      return null
    }

    return (
      <WidgetContainer
        config={{
          title,
          subtitle,
          backgroundColor,
          backgroundColorDisplay
        }}
        widgetName={widgetName}
      >
        {
          successMessageFor !== null
            ? (
              <Message
                variant='success'
                onDismiss={() => this.setState({ successMessageFor: null })}
                timer={5000}
              >
                <Phrase p={successMessageFor === 'joined' ? 'group.joined.successfully' : 'group.join.request.is.sent'} count={{ g: joinedGroupTitle }} />
              </Message>
              )
            : null
        }
        <div className='group-overview'>
          {
                groups.map((group) => (
                  <GroupItem
                    key={group.id}
                    displayUserStatus
                    onMembershipStatusChanged={(newMembershipStatus: membershipTypes) => (
                      this.setState({
                        successMessageFor: newMembershipStatus
                      })
                    )}
                    isEvenCount={groups.length === 2 || groups.length === 4}
                    {...group}
                    origin={ORIGIN_GROUP_WIDGET}
                    showGroupMeta={false}
                  />
                ))
              }
        </div>
        {buttonLabel && buttonURL &&
        // eslint-disable-next-line react/jsx-no-target-blank
          <a
            className='featured-topics__show-more-button'
            href={buttonURL}
            target={externalLink ? '_blank' : '_self'}
            rel={externalLink ? 'noreferrer noopener' : ''}
          >
            <Button
              title={buttonLabel}
            />
          </a>}
      </WidgetContainer>
    )
  }
}
